// =====================================================================================
// INVRT Radar — web console — Ask Radar (the default landing surface)
//
// A conversational front door over the same API calls the classic pages make. GUIDED MODE
// tonight: the routing is a rule matcher, not a model, and the panel says so on screen.
// See src/chat/loop.ts for the seam the model-driven loop lands behind.
//
// The money rule is identical to classic mode because it is the SAME component: a metered
// step renders QuoteApproveCard, and a person clicks approve on a card naming the exact
// cost. Nothing in this thread can spend a credit on its own.
// =====================================================================================

import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Send, Sparkles } from "lucide-react";
import { guidedLoop } from "@/chat/guidedLoop";
import type { AgentStep } from "@/chat/loop";
import { INTENT_SUGGESTIONS } from "@/chat/intents";
import { useApiClient, useBalance, usePricing, useSites } from "@/hooks/useApi";
import { useRevealFlow } from "@/hooks/useRevealFlow";
import { useSession } from "@/auth/SessionProvider";
import { QuoteApproveCard } from "@/components/QuoteApproveCard";
import { RevealedContacts } from "@/components/RevealedContacts";
import { TopLeadsPanel } from "@/components/TopLeadsPanel";
import { ErrorState, LoadingState } from "@/components/States";
import { splitList } from "@/components/FacetField";
import { Button, buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { formatCredits, formatCreditsWithUnit, humaniseAction, pluralise } from "@/lib/format";
import type { SearchResponse } from "@/lib/types";

interface ThreadItem {
  id: string;
  role: "person" | "radar";
  text?: string;
  step?: AgentStep;
}

let threadCounter = 0;
function nextId(): string {
  threadCounter += 1;
  return `thread-${threadCounter}`;
}

// ---- inline panels ---------------------------------------------------------------------

function BalancePanel() {
  const balance = useBalance();
  if (balance.isLoading) return <LoadingState label="Reading the ledger…" />;
  if (balance.isError) return <ErrorState error={balance.error} />;
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-2xl font-semibold text-ink">
        {formatCredits(balance.data?.balance)}
      </span>
      <span className="text-sm text-ink-muted">credits available</span>
    </div>
  );
}

function PricingPanel() {
  const pricing = usePricing();
  if (pricing.isLoading) return <LoadingState label="Reading your price list…" />;
  if (pricing.isError) return <ErrorState error={pricing.error} />;
  if (!pricing.data) return null;

  return (
    <ul className="flex flex-col gap-1.5">
      {pricing.data.actions.map((entry) => (
        <li key={entry.action} className="flex items-baseline justify-between gap-4 text-sm">
          <span className="text-ink">{humaniseAction(entry.action)}</span>
          <span className="font-mono text-ink-muted">
            {formatCreditsWithUnit(entry.credits_per_unit)} each
          </span>
        </li>
      ))}
    </ul>
  );
}

function SuppressionPanel() {
  return (
    <div className="flex flex-col items-start gap-3">
      <p className="text-sm text-ink-muted">
        Add the customers and contacts you already have, and I will never bill you to reveal
        or identify them again.
      </p>
      <Link to="/suppression" className={buttonVariants({ variant: "secondary", size: "sm" })}>
        Open my suppression list
      </Link>
    </div>
  );
}

function MenuPanel({ onPick }: { onPick: (text: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {INTENT_SUGGESTIONS.map((suggestion) => (
        <button
          key={suggestion.intent}
          type="button"
          onClick={() => onPick(suggestion.label)}
          className="rounded-full border border-line bg-surface px-3 py-1.5 text-sm text-ink transition-colors hover:border-primary hover:text-primary"
        >
          {suggestion.label}
        </button>
      ))}
    </div>
  );
}

function SitePickerPanel() {
  const sites = useSites();
  if (sites.isLoading) return <LoadingState label="Loading your sites…" />;
  if (sites.isError) return <ErrorState error={sites.error} />;
  if (!sites.data || sites.data.sites.length === 0) {
    return (
      <p className="text-sm text-ink-muted">
        No sites are set up yet, so there are no visitors to show.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {sites.data.sites.map((site) => (
        <li key={site.id} className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-ink">{site.domain}</span>
            <Badge tone={site.pixel_status === "verified" ? "success" : "neutral"}>
              {site.pixel_status === "verified" ? "Live" : "Pixel not confirmed"}
            </Badge>
          </div>
          <Link
            to={`/sites/${site.id}?tab=visitors`}
            className={buttonVariants({ variant: "secondary", size: "sm" })}
          >
            Show visitors
          </Link>
        </li>
      ))}
    </ul>
  );
}

function SetupSitePanel() {
  return (
    <div className="flex flex-col items-start gap-3">
      <p className="text-sm text-ink-muted">
        Adding a site creates the visitor-resolution account, records how consent is
        captured, and hands back an install snippet plus a guide for your platform.
      </p>
      <Link to="/sites" className={buttonVariants({ variant: "secondary", size: "sm" })}>
        Add a site
      </Link>
    </div>
  );
}

/** The one panel in the thread that can lead to a spend — through the shared approve card. */
function FindPeoplePanel() {
  const api = useApiClient();
  const balance = useBalance();
  const reveal = useRevealFlow();
  const [titles, setTitles] = useState("");
  const [countries, setCountries] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const search = useMutation({
    mutationFn: () =>
      api.post<SearchResponse>("/leads/search", {
        filters: {
          ...(splitList(titles).length > 0 ? { titles: splitList(titles) } : {}),
          ...(splitList(countries).length > 0
            ? { location: { countries: splitList(countries) } }
            : {}),
        },
        page: 1,
        per_page: 25,
      }),
    onSuccess: () => {
      setSelected(new Set());
      reveal.reset();
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          search.mutate();
        }}
      >
        <Input
          label="Job titles"
          value={titles}
          onChange={(event) => setTitles(event.target.value)}
          placeholder="Head of Compliance"
          hint="Separate several with commas."
        />
        <Input
          label="Countries"
          value={countries}
          onChange={(event) => setCountries(event.target.value)}
          placeholder="United Kingdom"
          hint="Separate several with commas."
        />
        <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
          <Button type="submit" loading={search.isPending}>
            Show me the matches
          </Button>
          <Link
            to="/prospecting"
            className={buttonVariants({ variant: "ghost", size: "md" })}
          >
            Use the full filters instead
          </Link>
        </div>
      </form>

      {search.isError ? <ErrorState error={search.error} /> : null}

      {search.isSuccess ? (
        search.data.rows.length === 0 ? (
          <p className="text-sm text-ink-muted">
            Nothing matched that. Try a broader title or drop the country.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-ink-muted">
                {search.data.total.toLocaleString("en-GB")} matches.{" "}
                {search.data.suppressed_count > 0
                  ? `${pluralise(search.data.suppressed_count, "contact")} hidden because you already have them.`
                  : "Previews are free — details cost credits."}
              </p>
              <Button
                size="sm"
                disabled={selected.size === 0}
                loading={reveal.phase === "quoting"}
                onClick={() => void reveal.quoteFor([...selected])}
              >
                Price {selected.size > 0 ? pluralise(selected.size, "contact") : "a selection"}
              </Button>
            </div>

            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell className="w-10">
                    <span className="sr-only">Select</span>
                  </TableHeaderCell>
                  <TableHeaderCell>Title</TableHeaderCell>
                  <TableHeaderCell>Company</TableHeaderCell>
                  <TableHeaderCell>Location</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {search.data.rows.map((row) => (
                  <TableRow key={row.provider_ref}>
                    <TableCell>
                      <label className="sr-only" htmlFor={`chat-select-${row.provider_ref}`}>
                        Select {row.title ?? row.provider_ref}
                      </label>
                      <input
                        id={`chat-select-${row.provider_ref}`}
                        type="checkbox"
                        checked={selected.has(row.provider_ref)}
                        onChange={() =>
                          setSelected((current) => {
                            const next = new Set(current);
                            if (next.has(row.provider_ref)) next.delete(row.provider_ref);
                            else next.add(row.provider_ref);
                            return next;
                          })
                        }
                      />
                    </TableCell>
                    <TableCell className="font-medium">{row.title ?? "—"}</TableCell>
                    <TableCell className="text-ink-muted">{row.company_name ?? "—"}</TableCell>
                    <TableCell className="text-ink-muted">{row.location ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : null}

      {reveal.phase === "failed" && reveal.error ? <ErrorState error={reveal.error} /> : null}

      {reveal.quote && (reveal.phase === "awaiting_approval" || reveal.phase === "revealing") ? (
        <QuoteApproveCard
          quote={reveal.quote}
          balance={balance.data?.balance ?? null}
          approving={reveal.phase === "revealing"}
          onApprove={() => void reveal.approve()}
          onCancel={reveal.cancel}
          {...(reveal.error ? { error: reveal.error } : {})}
        />
      ) : null}

      {reveal.result ? <RevealedContacts result={reveal.result} /> : null}
    </div>
  );
}

function StepRenderer({ step, onPick }: { step: AgentStep; onPick: (text: string) => void }) {
  switch (step.kind) {
    case "say":
      return null;
    case "balance":
      return <BalancePanel />;
    case "pricing":
      return <PricingPanel />;
    case "suppression":
      return <SuppressionPanel />;
    case "menu":
      return <MenuPanel onPick={onPick} />;
    case "form":
      switch (step.form.kind) {
        case "find_people":
          return <FindPeoplePanel />;
        case "site_visitors":
          return <SitePickerPanel />;
        case "setup_site":
          return <SetupSitePanel />;
        default:
          return null;
      }
    case "quote":
      // Reserved for the model-driven loop, which may hand back a quote it decided to
      // request. It renders the SAME approve card — see src/chat/loop.ts.
      return null;
    default:
      return null;
  }
}

// ---- page ------------------------------------------------------------------------------------

export function ChatPage() {
  const { activeMembership } = useSession();
  const sites = useSites();
  const [thread, setThread] = useState<ThreadItem[]>([]);
  const [draft, setDraft] = useState("");
  const [thinking, setThinking] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [thread]);

  const submit = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (trimmed === "") return;

      setThread((current) => [
        ...current,
        { id: nextId(), role: "person", text: trimmed },
      ]);
      setDraft("");
      setThinking(true);

      const turn = await guidedLoop.send(trimmed, {
        orgName: activeMembership?.organizations?.name ?? null,
        siteCount: sites.data?.sites.length ?? 0,
      });

      setThread((current) => [
        ...current,
        ...turn.steps.map((step): ThreadItem => ({
          id: nextId(),
          role: "radar",
          ...("text" in step ? { text: step.text } : {}),
          step,
        })),
      ]);
      setThinking(false);
    },
    [activeMembership, sites.data],
  );

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void submit(draft);
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Ask RADAR</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Say what you want in plain words. Everything metered still stops for your
            approval on a card naming the exact cost.
          </p>
        </div>
        <Badge tone="neutral" title="Rule-based routing. No model is involved.">
          <Sparkles className="h-3 w-3" aria-hidden="true" />
          {guidedLoop.mode} mode
        </Badge>
      </div>

      {thread.length === 0 ? (
        <>
          <Card>
            <CardHeader>
              <h2 className="text-base font-semibold text-ink">What would you like to do?</h2>
              <p className="text-sm text-ink-muted">
                RADAR finds the people worth contacting and names the companies already on
                your site. Pick one, or type it yourself.
              </p>
            </CardHeader>
            <CardContent>
              <MenuPanel onPick={(text) => void submit(text)} />
            </CardContent>
          </Card>
          {/* O1 (cert finding C6-F2): the global top leads live on the home surface. The
              panel reads the server's own ranked answer (the browser never re-ranks) and
              its copy follows the server's ranking marker. */}
          <Card>
            <CardContent className="pt-5">
              <TopLeadsPanel />
            </CardContent>
          </Card>
        </>
      ) : null}

      <div className="flex flex-col gap-4">
        {thread.map((item) =>
          item.role === "person" ? (
            <div key={item.id} className="flex justify-end">
              <p className="max-w-[80%] rounded-card rounded-br-sm bg-primary px-4 py-2.5 text-sm text-white">
                {item.text}
              </p>
            </div>
          ) : (
            <div key={item.id} className="flex flex-col gap-3">
              {item.text ? (
                <p className="max-w-[85%] rounded-card rounded-bl-sm bg-surface px-4 py-2.5 text-sm text-ink shadow-card">
                  {item.text}
                </p>
              ) : null}
              {item.step ? (
                <Card>
                  <CardContent className="pt-5">
                    <StepRenderer step={item.step} onPick={(text) => void submit(text)} />
                  </CardContent>
                </Card>
              ) : null}
            </div>
          ),
        )}
        {thinking ? <LoadingState label="Working out what you need…" /> : null}
        <div ref={endRef} />
      </div>

      <form onSubmit={handleSubmit} className="sticky bottom-4 flex items-end gap-2">
        <div className="flex-1">
          <label htmlFor="radar-chat-input" className="sr-only">
            What would you like RADAR to do?
          </label>
          <input
            id="radar-chat-input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Find heads of compliance at UK fintechs…"
            className="h-12 w-full rounded-full border border-line bg-surface px-5 text-sm text-ink shadow-card placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        </div>
        <Button type="submit" size="icon" className="h-12 w-12 rounded-full" aria-label="Send">
          <Send className="h-4 w-4" aria-hidden="true" />
        </Button>
      </form>
    </div>
  );
}
